import { BaseService } from './base.service'
import { ResponseWrapper, ErrorWrapper } from './util'

/**
 * Story 4930 — pagina publica de download de ebook (/e/{token}).
 *
 * O token da URL E a credencial. Nao ha sessao, nao ha login, nao ha X-ApplicationId.
 */
export class EbookAccessService extends BaseService {
  static get entity () { return 'ebook-access' }

  /**
   * Instancia axios garantidamente SEM `Authorization`.
   *
   * O projeto seta o Bearer em `axios.defaults.headers.common` (auth.service.js:194 e o
   * guard em router/middlewares.js:29). Esse default e copiado para toda instancia criada
   * por `axios.create()`, inclusive as de `{ auth: false }` — entao um aluno que esteja
   * navegando logado e clique no link do e-mail enviaria o Bearer de outro contexto para
   * uma rota anonima.
   *
   * Medido no axios 0.19.2 deste repo:
   *   - `{ headers: { authorization: null } }`      -> envia a string "[object Object]"
   *   - `{ headers: { authorization: undefined } }` -> envia "undefined"
   *   - interceptor de request apagando cfg.headers -> nao funciona, o merge vem depois
   *   - `create({ headers: { common: {} } })`       -> nao funciona
   *   - apagar dos defaults DA INSTANCIA            -> funciona, e o global fica intacto
   */
  static anonimo () {
    const http = this.request({ auth: false });

    delete http.defaults.headers.common.authorization;
    delete http.defaults.headers.common.Authorization;

    return http;
  }

  /**
   * Resolve o token e monta a pagina.
   *
   * 200 SEMPRE que o token existe — inclusive revogado, que vem com `valid: false`.
   * Isso NAO e erro: a tela de "link invalido" precisa do branding e do reenvio, e por
   * isso o backend devolve `productName` e `branding` preenchidos nesse caso.
   * So cai no catch em 404 (token nunca existiu) ou falha de rede.
   */
  static async resolver (token) {
    try {
      const response = await this.anonimo()
        .get(`${process.env.VUE_APP_EAD_API_URL}/ebook-access/${token}`);

      return new ResponseWrapper({ status: 200, data: { success: true } }, response.data);
    } catch (error) {
      const status = error.response ? error.response.status : null;

      if (status === 404) throw new ErrorWrapper(error, 'link-inexistente');
      if (!error.response) throw new ErrorWrapper(error, 'sem-conexao');

      throw new ErrorWrapper(error, error.message);
    }
  }

  /**
   * URL absoluta de download. O `downloadUrl` vem montado pela API — usar como veio.
   * Remontar pelo learningObjectId quebra quando os ids mudam (aconteceu entre a captura
   * do contrato e a validacao: 74462 virou 74463).
   */
  static urlDeDownload (downloadUrl) {
    return `${process.env.VUE_APP_EAD_API_URL}${downloadUrl}`;
  }

  /**
   * Reenvia o link para o e-mail da compra. Sem body — o destinatario nao e escolhido
   * pelo front.
   *
   * 429 nao e falha: e "aguarde". A janela e de 5 min nesta rota e o carimbo e por
   * matricula, compartilhado com o reenvio do suporte no painel.
   */
  static async reenviar (token) {
    try {
      const response = await this.anonimo()
        .post(`${process.env.VUE_APP_EAD_API_URL}/ebook-access/${token}/resend`, null);

      return new ResponseWrapper({ status: 200, data: { success: true } }, response.data);
    } catch (error) {
      const resposta = error.response;
      const status = resposta ? resposta.status : null;

      if (status === 429) {
        // O header `Retry-After` (segundos) e o valor autoritativo, mas so passou a ser
        // exposto no CORS pelo commit d0cafa3 do WebApi. Enquanto o deploy nao sai ele
        // chega null no navegador e o corpo (`retryAfterMinutes`) assume.
        const doHeader = Number(resposta.headers && resposta.headers['retry-after']);
        const doCorpo = resposta.data && resposta.data.retryAfterMinutes;
        const segundos = doHeader || (doCorpo ? Number(doCorpo) * 60 : 300);

        throw new ErrorWrapper(error, `aguarde-${segundos}`);
      }

      // 503: nada foi enviado e a janela NAO foi consumida — pode tentar na hora.
      if (status === 503) throw new ErrorWrapper(error, 'tente-novamente');

      throw new ErrorWrapper(error, error.message);
    }
  }
}
